<script>
  import { Icon } from "./ui/index.js";

  /**
   * @typedef {Object} Notice
   * @property {string} id
   * @property {'banner'|'modal'} type
   * @property {'info'|'warning'|'error'} severity
   * @property {string} title
   * @property {string} message
   * @property {string} [linkText]
   * @property {string} [linkUrl]
   * @property {boolean} dismissable
   * @property {number} priority
   */

  /** @type {{ notices: Notice[] }} */
  let { notices = [] } = $props();

  // Track dismissed notices in localStorage
  const DISMISSED_KEY = "pd_dismissed_notices";

  function getDismissedNotices() {
    try {
      const stored = localStorage.getItem(DISMISSED_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  function dismissNotice(id) {
    const dismissed = getDismissedNotices();
    if (!dismissed.includes(id)) {
      dismissed.push(id);
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(dismissed));
    }
    // Force reactivity by updating local state
    dismissedIds = [...dismissed];
  }

  // Local state for dismissed notices
  let dismissedIds = $state(getDismissedNotices());

  // Filter to only banner notices that aren't dismissed
  let visibleBanners = $derived(
    notices
      .filter((n) => n.type === "banner")
      .filter((n) => !n.dismissable || !dismissedIds.includes(n.id))
  );

  // Severity styles
  const severityStyles = {
    info: {
      bg: "var(--color-primary-50)",
      border: "var(--color-primary-200)",
      icon: "var(--color-primary-600)",
      text: "var(--color-primary-900)",
    },
    warning: {
      bg: "#fff8e6",
      border: "#f59e0b",
      icon: "#d97706",
      text: "#92400e",
    },
    error: {
      bg: "#fef2f2",
      border: "#ef4444",
      icon: "#dc2626",
      text: "#991b1b",
    },
  };

  const severityIcons = {
    info: "info",
    warning: "alertTriangle",
    error: "alertCircle",
  };
</script>

{#if visibleBanners.length > 0}
  <div class="notices-container">
    {#each visibleBanners as notice (notice.id)}
      {@const styles = severityStyles[notice.severity] || severityStyles.info}
      <div
        class="notice-banner"
        style="background: {styles.bg}; border-color: {styles.border};"
      >
        <div class="notice-icon" style="color: {styles.icon};">
          <Icon name={severityIcons[notice.severity] || "info"} size={20} />
        </div>
        <div class="notice-content">
          {#if notice.title}
            <div class="notice-title" style="color: {styles.text};">
              {notice.title}
            </div>
          {/if}
          <div class="notice-message" style="color: {styles.text};">
            {notice.message}
          </div>
          {#if notice.linkText && notice.linkUrl}
            <a
              href={notice.linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              class="notice-link"
            >
              {notice.linkText}
              <Icon name="externalLink" size={14} />
            </a>
          {/if}
        </div>
        {#if notice.dismissable}
          <button
            class="notice-dismiss"
            onclick={() => dismissNotice(notice.id)}
            aria-label="Dismiss notice"
          >
            <Icon name="x" size={18} />
          </button>
        {/if}
      </div>
    {/each}
  </div>
{/if}

<style>
  .notices-container {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    width: 100%;
    max-width: 600px;
  }

  .notice-banner {
    display: flex;
    align-items: flex-start;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-lg);
    border: 1px solid;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .notice-icon {
    flex-shrink: 0;
    margin-top: 2px;
  }

  .notice-content {
    flex: 1;
    min-width: 0;
  }

  .notice-title {
    font-weight: var(--font-semibold);
    font-size: var(--text-sm);
    margin-bottom: var(--space-1);
  }

  .notice-message {
    font-size: var(--text-sm);
    line-height: 1.5;
  }

  .notice-link {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
    margin-top: var(--space-2);
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    color: inherit;
    text-decoration: underline;
    opacity: 0.9;
  }

  .notice-link:hover {
    opacity: 1;
  }

  .notice-dismiss {
    flex-shrink: 0;
    padding: var(--space-1);
    background: transparent;
    border: none;
    cursor: pointer;
    opacity: 0.5;
    transition: opacity 0.2s;
    color: inherit;
  }

  .notice-dismiss:hover {
    opacity: 1;
  }
</style>
